import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { ReactNode } from 'react'
import type { SalesOrder } from '@/lib/api/sales-order-api'
import type {
    StationNewOrderEvent,
    StationNotificationEvent,
    StationWorkActivityEvent,
} from '@/hooks/use-station-sse'

import { StationFleetPanel } from '@/components/station/station-fleet-panel'
import { StationScreenSwitcher } from '@/components/station/station-screen-switcher'
import { StationWorkBoard } from '@/components/station/station-work-board'
import { groupStationWork } from '@/components/station/station-work-model'
import { useNow } from '@/hooks/use-now'
import { useStationScreenRotation } from '@/hooks/use-station-screen-rotation'
import { useTemporaryHighlight } from '@/hooks/use-temporary-highlight'
import { useStationWorkActivities } from '@/hooks/use-work-activities-query'
import { StationShell, SummaryStat } from '@/components/station/station-shell'
import { StationOrderDetailDialog } from '@/components/station/station-order-detail-dialog'
import { useAuth } from '@/hooks/use-auth'
import { useFleet } from '@/hooks/use-fleet-query'
import { useMinWidth } from '@/hooks/use-min-width'
import { useStationAudio } from '@/hooks/use-station-audio'
import { useStationEventTimes } from '@/hooks/use-station-event-times'
import { useStationReadinessHeartbeat } from '@/hooks/use-station-readiness-heartbeat'
import { useStationSSE } from '@/hooks/use-station-sse'
import { useSalesOrders } from '@/hooks/use-sales-order-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import {
    orderActivityTime,
    rememberEventTime,
    sortOrdersByActivity,
} from '@/lib/station-order-sort'
import { buildNewOrderToast } from '@/lib/station-tts'

/** Thời gian tô sáng một đơn vừa được đọc loa. */
const HIGHLIGHT_MS = 5_000

export const Route = createFileRoute('/_app/_authed/station')({
    component: StationPage,
})

function StationPage() {
    // _authed beforeLoad guarantees user is authenticated before this renders
    return <StationView />
}

export function StationView() {
    const navigate = useNavigate()
    const exitKiosk = () => navigate({ to: '/', search: { kioskExit: true } })
    const {
        soundEnabled,
        audioBlocked,
        enableSound,
        disableSound,
        playNewOrderAlert,
        playNotificationAlert,
        playWorkEventReminder,
        playTestSound,
    } = useStationAudio()
    const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set())
    // Mốc sự kiện gần nhất theo đơn — quyết định thứ tự bảng. Lưu qua localStorage
    // để F5 giữa ca không xoá sạch thứ tự đã tích luỹ.
    const [eventTimes, setEventTimes] = useStationEventTimes()
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
    const highlightTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
        new Map(),
    )
    // Mốc hoạt động cao nhất đang hiển thị, dùng làm sàn cho sự kiện thiếu
    // `occurred_at`: đồng hồ tablet có thể chậm hơn server, tin `Date.now()`
    // thì đơn vừa được đọc loa lại không nhích lên.
    const maxActivityRef = useRef(0)

    useEffect(() => {
        const timers = highlightTimers.current
        return () => {
            for (const timer of timers.values()) clearTimeout(timer)
            timers.clear()
        }
    }, [])

    // Ghi nhận một đơn vừa có hoạt động: đẩy nó lên đầu bảng và tô sáng để
    // nhân viên nghe loa xong nhìn lên là thấy ngay.
    const markActivity = useCallback(
        (orderId: string | undefined, occurredAt: string | undefined) => {
            if (!orderId) return
            // +1ms để chắc chắn vượt lên trên đơn đang đứng đầu khi phải dùng
            // mốc dự phòng.
            const fallback = Math.max(Date.now(), maxActivityRef.current + 1)
            setEventTimes((prev) =>
                rememberEventTime(prev, orderId, occurredAt, fallback),
            )
            setHighlightedIds((prev) => new Set(prev).add(orderId))
            // Sự kiện thứ hai cho cùng đơn phải gia hạn vệt sáng, nếu không hẹn
            // giờ cũ vẫn nổ và tắt sáng sớm.
            const running = highlightTimers.current.get(orderId)
            if (running) clearTimeout(running)
            const timer = setTimeout(() => {
                setHighlightedIds((prev) => {
                    const next = new Set(prev)
                    next.delete(orderId)
                    return next
                })
                highlightTimers.current.delete(orderId)
            }, HIGHLIGHT_MS)
            highlightTimers.current.set(orderId, timer)
        },
        [],
    )

    const { hasPermission } = useAuth()
    // Bảng công việc: nạp nền mỗi phút, còn SSE lo phần nhảy màn tức thì.
    //
    // Gác quyền ngay ở đây vì /station chỉ chặn bằng đăng nhập, không chặn theo
    // vai trò: tài khoản không có `work_activities.read_station` mà không gác
    // sẽ nện 403 vào endpoint này mỗi phút suốt ca. Cùng cách làm với useFleet
    // ở dưới.
    const canReadWork = hasPermission('work_activities.read_station')
    const { data: workActivities } = useStationWorkActivities(canReadWork)
    // Đồng hồ riêng: vạch "bây giờ" và các con số đếm ngược phải nhích theo thời
    // gian thật, không chỉ khi có sự kiện làm màn render lại.
    const workNow = useNow()
    // Hàng vừa có biến động sáng lên như hàng đơn mới ở bảng đơn.
    const { ids: workArrivedIds, mark: markWorkArrived } =
        useTemporaryHighlight(HIGHLIGHT_MS)
    const workGroups = groupStationWork(workActivities ?? [], workNow)
    const workHasContent =
        workGroups.schedule.length +
            workGroups.trips.length +
            workGroups.tasks.length >
        0

    // `showFleet` khai ở dưới (cần quyền + bề rộng) nên vòng xoay nhận nó qua
    // biến trung gian: hook phải biết màn định vị có tới lượt được không.
    const isWide = useMinWidth(1280)
    const canShowFleet = hasPermission('vehicles.read') && isWide

    const { screen, selectable, selectScreen, signalOrders, signalWork } =
        useStationScreenRotation({
            workHasContent,
            fleetHasContent: canShowFleet,
            // Nút "Công việc" chỉ có khi tài khoản đọc được bảng: không có quyền
            // thì bảng luôn rỗng, bấm vào chỉ thấy "không còn lịch" sai sự thật.
            canPickWork: canReadWork,
        })

    const handleNewOrder = useCallback(
        (event: StationNewOrderEvent) => {
            playNewOrderAlert(event)
            // Key by order_id so a burst of orders (or a redelivered event)
            // updates one toast per order instead of stacking duplicates.
            toast.success(buildNewOrderToast(event), { id: event.order_id })
            if (event.speech_template) {
                markActivity(event.order_id, event.occurred_at)
            }
            // Loa vừa đọc đơn mới thì màn phải là bảng đơn: người ở kho ngẩng
            // lên là thấy đúng đơn vừa được gọi.
            signalOrders()
        },
        [playNewOrderAlert, markActivity, signalOrders],
    )

    const handleNotification = useCallback(
        (event: StationNotificationEvent) => {
            playNotificationAlert(event)
            // Chỉ xếp lại bảng khi sự kiện thật sự được đọc loa. Server chỉ đính
            // `speech_template` khi case đó đang bật trong cấu hình giọng nói,
            // nên không có nó nghĩa là đơn nhảy lên đầu trong im lặng.
            if (!event.speech_template) return
            // Soạn xong / nhận tiền xảy ra hàng giờ sau khi tạo đơn, nên đơn
            // phải được xếp lại theo mốc sự kiện chứ không theo giờ tạo.
            markActivity(event.order_id, event.occurred_at)
            signalOrders()
        },
        [playNotificationAlert, markActivity, signalOrders],
    )

    // Biến động bên công việc: tô sáng đúng hàng đó rồi nhảy sang bảng công
    // việc. Chỉ nhảy mà không tô sáng thì màn đổi nhưng không ai biết dòng nào
    // vừa đổi.
    //
    // Hai mốc nhắc sự kiện thì đọc loa thêm (#M-154): màn hình chung là nơi cả
    // kho nghe thấy, kể cả người không mở chuông thông báo.
    const handleWorkActivity = useCallback(
        (event: StationWorkActivityEvent) => {
            markWorkArrived(event.work_activity_id)
            signalWork()
            playWorkEventReminder(event)
        },
        [markWorkArrived, signalWork, playWorkEventReminder],
    )

    // Mẻ sổ xưởng (#M-16) KHÔNG đọc ở đây nữa: đã tách sang máy trạm kho
    // (`/station/warehouse`), đúng người nghe và không đọc xen vào bảng đơn.

    const {
        connected: sseConnected,
        dataStatus,
        refetch,
    } = useStationSSE(
        handleNewOrder,
        handleNotification,
        undefined,
        undefined,
        handleWorkActivity,
    )
    // Every signed-in account on this screen is a station; the server-side
    // checker decides which ones are monitored.
    const readiness = useStationReadinessHeartbeat({
        sseConnected,
        soundEnabled,
        audioBlocked,
    })

    // Wake Lock — prevent tablet screen from sleeping
    useEffect(() => {
        let wakeLock: WakeLockSentinel | null = null
        const request = async () => {
            try {
                wakeLock = await navigator.wakeLock.request('screen')
            } catch {
                /* not supported or denied */
            }
        }
        void request()
        const onVisible = () => {
            if (document.visibilityState === 'visible') void request()
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            void wakeLock?.release()
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [])

    const { data } = useSalesOrders({
        status: 'confirmed' as const,
        per_page: 100,
    })

    const { data: stockOutData } = useSalesOrders({
        status: 'stock_out' as const,
        per_page: 100,
    })

    // Màn định vị/camera chỉ dành cho tài khoản có quyền xem xe. Không có quyền
    // thì không gọi fleet (tránh 403 lặp) và màn đó không có trong vòng xoay.
    // Vẫn chặn theo bề rộng: dưới 1280px panel không mount, nên không mở stream
    // camera live vô hình trên tablet nhỏ (iPad landscape < 1280px).
    const showFleet = canShowFleet
    const { data: fleet, isFetching: fleetFetching } = useFleet(showFleet)
    const fleetVehicles = showFleet ? (fleet ?? []) : []

    const orders = useMemo(
        () =>
            sortOrdersByActivity(
                [...(data?.data ?? []), ...(stockOutData?.data ?? [])],
                eventTimes,
            ),
        [data?.data, stockOutData?.data, eventTimes],
    )

    useEffect(() => {
        maxActivityRef.current = orders.reduce(
            (max, order) => Math.max(max, orderActivityTime(order, eventTimes)),
            0,
        )
    }, [orders, eventTimes])

    const waitingCount = useMemo(
        () => orders.filter((o) => o.status === 'confirmed').length,
        [orders],
    )
    const readyCount = orders.length - waitingCount

    return (
        <StationShell
            // Tiêu đề, nhãn kết nối và rail chân đổi theo màn: giữ nguyên của
            // bảng đơn thì màn công việc trông như đang hiện nhầm nội dung, và
            // hai rail chân chồng lên nhau.
            title={
                screen === 'work'
                    ? m.station_work_title()
                    : screen === 'fleet'
                      ? m.station_fleet_screen_title()
                      : m.station_title()
            }
            liveLabel={
                screen === 'work'
                    ? m.station_work_live()
                    : screen === 'fleet'
                      ? m.station_fleet_live()
                      : undefined
            }
            connected={sseConnected}
            dataStatus={dataStatus}
            onRetryData={() => {
                void refetch()
            }}
            soundEnabled={soundEnabled}
            audioBlocked={audioBlocked}
            onToggleSound={soundEnabled ? disableSound : enableSound}
            onTestSound={playTestSound}
            onExit={exitKiosk}
            readinessNotice={
                readiness.blocked && readiness.blockedAt
                    ? {
                          blockedAt: readiness.blockedAt,
                          requestId: readiness.requestId,
                      }
                    : undefined
            }
            footer={
                screen !== 'orders' ? undefined : (
                    <footer className="station-chrome flex items-center justify-between gap-4 border-t border-[var(--line)] px-5 py-2.5 text-xs">
                        <div className="flex items-center gap-5">
                            <SummaryStat
                                tone="amber"
                                count={waitingCount}
                                label={m.station_summary_waiting()}
                            />
                            <SummaryStat
                                tone="ready"
                                count={readyCount}
                                label={m.station_summary_ready()}
                            />
                        </div>
                        <span className="font-semibold uppercase tracking-[0.18em] text-[var(--dim)]">
                            {m.station_summary_total({ count: orders.length })}
                        </span>
                    </footer>
                )
            }
        >
            {/* BA màn full, đảo nhau 30 giây một lần; biến động bên đơn hoặc
                bên công việc thì nhảy sang ngay, còn màn định vị xe chỉ đến khi
                đủ lượt. Màn nào không có nội dung thì không tới lượt.

                Cả ba đều MOUNT sẵn và chỉ ẩn/hiện bằng `display`: màn định vị
                mở stream camera live (autoStart), unmount mỗi vòng là cứ 30
                giây lại dựng lại stream từ đầu. Bảng đơn cũng giữ được chỗ cuộn.
                Dùng `style` chứ không phải class `hidden`: khối nào cũng có sẵn
                class `flex`, hai class display cùng lúc thì thứ tự trong file
                CSS quyết định — đúng loại lỗi không thấy được khi đọc code.

                Dải nút ở trên cho người ở kho sang ngay màn cần xem; bấm xong
                15 giây vòng xoay mới chạy lại (FD-393). */}
            <StationScreenSwitcher
                screen={screen}
                screens={selectable}
                onSelect={selectScreen}
            />
            <Block active={screen === 'orders'}>
                <main className="min-w-0 flex-1 overflow-auto">
                    {orders.length === 0 ? (
                        <div className="flex h-full items-center justify-center px-6">
                            <p className="empty-blink text-center text-xl tracking-[0.3em] text-[var(--dim)]">
                                —&nbsp;&nbsp;{m.station_no_orders()}
                                &nbsp;&nbsp;—
                            </p>
                        </div>
                    ) : (
                        <div className="min-w-[980px]">
                            {/* Dải legend */}
                            <div className="station-chrome board-grid legend">
                                <span>{m.order_time()}</span>
                                <span>{m.order_code()}</span>
                                <span>{m.customer_title()}</span>
                                <span>{m.order_shipping_address()}</span>
                                <span className="text-right">
                                    {m.order_total()}
                                </span>
                                <span className="text-center">
                                    {m.order_status()}
                                </span>
                            </div>

                            <div className="board-list">
                                {orders.map((order) => (
                                    <StationRow
                                        key={order.id}
                                        order={order}
                                        activityAt={orderActivityTime(
                                            order,
                                            eventTimes,
                                        )}
                                        isHighlighted={highlightedIds.has(
                                            order.id,
                                        )}
                                        onClick={() =>
                                            setSelectedOrderId(order.id)
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </main>
            </Block>

            {showFleet && (
                <Block active={screen === 'fleet'}>
                    <aside className="station-fleet-aside flex min-w-0 flex-1">
                        <StationFleetPanel
                            vehicles={fleetVehicles}
                            isFetching={fleetFetching}
                        />
                    </aside>
                </Block>
            )}

            <Block active={screen === 'work'}>
                <StationWorkBoard
                    activities={workActivities ?? []}
                    now={workNow}
                    arrivedIds={workArrivedIds}
                />
            </Block>

            <StationOrderDetailDialog
                orderId={selectedOrderId}
                onClose={() => setSelectedOrderId(null)}
            />
        </StationShell>
    )
}

/** Một block full màn. Ẩn bằng `display` để giữ nguyên stream camera và chỗ
 *  cuộn của bảng khi màn khác đang tới lượt. */
function Block({ active, children }: { active: boolean; children: ReactNode }) {
    return (
        <div
            className="flex min-h-0 flex-1"
            style={active ? undefined : { display: 'none' }}
        >
            {children}
        </div>
    )
}

function StationRow({
    order,
    activityAt,
    isHighlighted,
    onClick,
}: {
    order: SalesOrder
    activityAt: number
    isHighlighted: boolean
    onClick: () => void
}) {
    const isConfirmed = order.status === 'confirmed'
    const statusLabel = isConfirmed
        ? m.status_order_confirmed()
        : m.status_order_stock_out()

    // Mốc hoạt động, không phải giờ tạo — cùng khoá với thứ tự sắp xếp. Mốc 0
    // nghĩa là dữ liệu ngày hỏng: hiện gạch ngang thay vì "07:00" trông như thật.
    const timeStr = activityAt
        ? new Date(activityAt).toLocaleTimeString('vi-VN', {
              hour: '2-digit',
              minute: '2-digit',
          })
        : '—'

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onClick()
                }
            }}
            className={`board-grid board-row ${
                isConfirmed ? 'confirmed' : 'stockout'
            } ${isHighlighted ? 'arrive' : ''}`}
        >
            {/* Giờ */}
            <span className="text-base tabular-nums text-[var(--dim)]">
                {timeStr}
            </span>

            {/* Mã đơn — mỏ neo của cả hàng */}
            <span className="overflow-hidden text-ellipsis whitespace-nowrap text-lg font-black tracking-wide text-[var(--ink)]">
                {order.order_code}
            </span>

            {/* Khách hàng */}
            <span className="truncate text-base font-bold text-[var(--ink)]">
                {order.customer_name}
            </span>

            {/* Địa chỉ giao */}
            <span className="truncate text-base font-bold text-[var(--dim)]">
                {order.shipping_address || '—'}
            </span>

            {/* Tổng tiền */}
            <span className="text-right text-base font-bold tabular-nums text-[var(--ink)]">
                {formatVnd(order.total_amount)}
            </span>

            {/* Trạng thái — thẻ cổng */}
            <span className="flex justify-center">
                <span
                    className={`gate-tag ${
                        isConfirmed ? 'gate-amber' : 'gate-ready'
                    }`}
                >
                    {statusLabel}
                </span>
            </span>
        </div>
    )
}
